import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import { supplierInventoryApi, supplierInventoryImportApi } from "@/main/api";
import ContainerScreen from "@/main/components/shared/containerScreen/containerLayout";
import LoadingComponent from "@/main/components/shared/loading/loadingComponent";
import ResultNotFoundComponent from "@/main/components/shared/resultNotFound/resultNotFoundComponent";
import { ImportSaveRequest, SupplierInventoryRequest } from "@/main/models/request";
import { SupplierInventoryDataResponse } from "@/main/models/response";
import { PartCatalogSuggestionResponse } from "@/main/models/response/partCatalogResponse";
import { getTranslateByKey, isNullUndefined, removeEmptyValues } from "@/main/services/utils";
import { getCountriesUser, useCountryReducer } from "@/main/store/country";

import SectionFooter from "./sectionFooter/sectionFooter";
import SectionHistory from "./sectionHistory/sectionHistory";
import SectionMappingParts from "./sectionMappingParts/sectionMappingParts";
import SectionPartDetail from "./sectionPartDetail/sectionPartDetail";
import SectionPriceList, { IDataPriceList } from "./sectionPriceList/sectionPriceList";
import SectionSupplier from "./sectionSupplier/sectionSupplier";
import SectionVehicles, { ISectionVehiclesState } from "./sectionVehicles/sectionVehicles";
import {
	createImagesRequest,
	createPartsRequest,
	hasShowAllPage,
	IInventoryRegisterForm,
	INITIAL_FORM_STATE,
	INITIAL_SUGGESTION,
} from "./utilsInventoryRegisterScreen";
import * as S from "./styles";

function InventoryRegisterScreen() {
	const dispatch = useDispatch();

	const [searchParams, setSearchParams] = useSearchParams();
	const guidSupplierInventoryParams = searchParams.get("guidSupplierInventory");
	const guidSupplierInventoryWarehouseParams = searchParams.get("guidSupplierInventoryWarehouse");
	const { countriesUser: countryReducer } = useCountryReducer();

	const methodsInventoryRegisterScreen = useForm<IInventoryRegisterForm>({
		defaultValues: INITIAL_FORM_STATE,
		shouldFocusError: true,
	});

	//#region States
	const [imagesDelete, setImagesDelete] = useState<string[]>([]);
	const [dataInventory, setDataInventory] = useState<SupplierInventoryDataResponse | null>(null);
	const [imagesPart, setImagesPart] = useState<File[]>([]);
	const [vehicles, setVehicles] = useState<ISectionVehiclesState[]>([]);
	const [vehiclesDelete, setVehiclesDelete] = useState<string[]>([]);
	const [dataPriceList, setDataPriceList] = useState<IDataPriceList[]>([]);
	const [isEdit, setIsEdit] = useState(!isNullUndefined(guidSupplierInventoryParams));
	const [suggestionFields, setSuggestionFields] = useState<PartCatalogSuggestionResponse>(INITIAL_SUGGESTION);
	const [isLoadingSave, setIsLoadingSave] = useState(false);
	const [isLoadingEdit, setIsLoadingEdit] = useState<boolean>(false);
	const [vehiclesAPI, setVehiclesAPI] = useState<ISectionVehiclesState[]>([]);

	const watcherCountry = methodsInventoryRegisterScreen.watch("country");
	//#endregion

	//#region Core Functions
	function changeScreenToEdit(guidSupplierInventory: string, guidSupplierInventoryWarehouse: string) {
		setIsEdit(true);
		setSearchParams({ guidSupplierInventory, guidSupplierInventoryWarehouse }, { replace: true });
	}
	//#endregion

	//#region Async Functions
	async function loadInventory(guidSupplierInventoryWarehouseRequest: string) {
		try {
			setIsLoadingEdit(true);
			const request: SupplierInventoryRequest = {
				guidSupplierInventoryWarehouse: guidSupplierInventoryWarehouseRequest,
			};
			const response = await supplierInventoryApi.getInfoPart(request);
			const resultData = response.data.result;

			setDataInventory(resultData);
			setVehicles([]);
			setVehiclesAPI([]);

			return response.data.result;
		} catch {
		} finally {
			setIsLoadingEdit(false);
		}
	}

	async function handleSaveInventory() {
		setIsLoadingSave(true);

		const data = methodsInventoryRegisterScreen.getValues();

		const imagesRequest = await createImagesRequest(imagesPart);
		const partsRequest = createPartsRequest(dataPriceList);

		const requestData: ImportSaveRequest = {
			// Supplier Information
			guidCountry: String(data.country?.key),
			guidSupplier: String(data.supplier?.key),
			guidWarehouse: String(data.warehouse?.key),
			guidSupplierInventoryWareHouse: String(guidSupplierInventoryWarehouseParams),
			// Part Mapping
			guidPartCategory: String(data.category?.key),
			guidPartSubCategory: String(data.subCategory?.key),
			xnuupPartId: Number(data.xnuupPartId?.id),
			guidPartPosition: String(data.position?.key),
			// Part Detail
			guidPartType: String(data.type?.key),
			guidSupplierInventory: String(guidSupplierInventoryParams),
			partCode: String(data?.sku),
			partTitle: String(data?.name),
			partDescription: String(data?.observation),
			guidPackageType: String(data?.packageContent?.key),
			stock: Number(data.amount),
			weight: data.weight,
			dimensions: String(data?.dimensions),
			color: String(data?.color),
			warranty: Number(data.warrantyPeriod),
			guidPartBrand: String(data?.partBrand?.key),
			pickupTime: Number(data.pickupTime),
			imagesForDelete: imagesDelete,
			partNumberOEM: data?.partNumberOem,
			partNumberIAM: data?.partNumberIam,
			// Vehicles
			vehicles: vehicles,
			vehiclesDelete: vehiclesDelete,
			//Prices
			parts: partsRequest,
			...imagesRequest,
		};

		supplierInventoryImportApi
			.saveImport(removeEmptyValues(requestData))
			.then((response) => {
				toast.success(getTranslateByKey("information.inventorySavedSuccessfully"));

				const result = response.data.result;
				if (!isEdit) return changeScreenToEdit(result.guidSupplierInventory, result.guidSupplierInventoryWarehouse);

				loadInventory(result.guidSupplierInventoryWarehouse);
			})
			.finally(() => {
				setIsLoadingSave(false);
				setImagesDelete([]);
			});
	}
	//#endregion

	useEffect(() => {
		if (isNullUndefined(countryReducer.data)) dispatch(getCountriesUser());
	}, []);

	useEffect(() => {
		if (!isNullUndefined(guidSupplierInventoryParams) && !isNullUndefined(guidSupplierInventoryWarehouseParams)) {
			loadInventory(guidSupplierInventoryWarehouseParams);
		}
	}, [guidSupplierInventoryParams, guidSupplierInventoryWarehouseParams]);

	const titlePage = `${getTranslateByKey("module.inventory")} > ${getTranslateByKey("information.registerInventory")}`;

	if (isLoadingEdit) return <LoadingComponent />;
	if (isEdit && isNullUndefined(dataInventory)) return <ResultNotFoundComponent />;

	const showAllPage = hasShowAllPage(methodsInventoryRegisterScreen, [...vehicles, ...vehiclesAPI]);

	return (
		<FormProvider {...methodsInventoryRegisterScreen}>
			<ContainerScreen title={titlePage}>
				<S.Container>
					<SectionSupplier isEdit={isEdit} dataInventory={dataInventory} watcherCountry={watcherCountry} />
					<SectionMappingParts dataInventory={dataInventory} />
					<SectionVehicles
						guidCountry={watcherCountry?.key}
						countryName={watcherCountry?.value}
						dataInventory={dataInventory}
						guidSupplierInventory={guidSupplierInventoryParams}
						vehicles={vehicles}
						vehiclesAPI={vehiclesAPI}
						setVehiclesDelete={setVehiclesDelete}
						setVehicles={setVehicles}
						setVehiclesAPI={setVehiclesAPI}
						setSuggestionFields={setSuggestionFields}
					/>
					{showAllPage && (
						<>
							<SectionPartDetail
								imagesPart={imagesPart}
								dataInventory={dataInventory}
								watcherCountry={watcherCountry}
								setImagesDelete={setImagesDelete}
								setImagesPart={setImagesPart}
							/>
							<SectionPriceList
								watcherCountry={watcherCountry}
								dataInventory={dataInventory}
								dataPriceList={dataPriceList}
								setDataPriceList={setDataPriceList}
							/>

							{isEdit && (
								<SectionHistory dataInventory={dataInventory} guidSupplierInventory={guidSupplierInventoryParams} />
							)}
							<SectionFooter
								handleSubmitInventory={handleSaveInventory}
								isLoadingSave={isLoadingSave}
								setIsLoadingSave={setIsLoadingSave}
								suggestionFields={suggestionFields}
								vehicles={[...vehicles, ...vehiclesAPI]}
							/>
						</>
					)}
				</S.Container>
			</ContainerScreen>
		</FormProvider>
	);
}

export default InventoryRegisterScreen;
